import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ChamberMemberProPagePermissionsEnum, UserType } from "./graphql/types";
import { NETWORK_ERROR_TEXT, STAFF_SUPPORT_EMAIL } from "./utils/userFacingError";
import Sample from "./components/Sample";
import ChamberSettings from "./components/pages/MyChamber/ChamberSettings";
import ManageChamberProfile from "./components/pages/MyChamber/ManageChamberProfile";
import CreateMember from "./components/pages/Members/CreateMember/CreateMember";
import ManageMembers from "./components/pages/Members/ManageMembers/ManageMembers";
import UpdateMember from "./components/pages/Members/UpdateMember/UpdateMember";
import CreateProspect from "./components/pages/Prospects/CreateProspect/CreateProspect";
import ManageProspects from "./components/pages/Prospects/ManageProspects/ManageProspects";
import UpdateProspect from "./components/pages/Prospects/UpdateProspect/UpdateProspect";
import CreateEvent from "./components/pages/Events/CreateEvent/CreateEvent";
import ManageEvents from "./components/pages/Events/ManageEvents/ManageEvents";
import UpdateEvent from "./components/pages/Events/UpdateEvent/UpdateEvent";
import InvoiceManageTicketSales from "./components/pages/Events/EventSales/InvoiceManageTicketSales";
import InvoiceManageSponsorshipSales from "./components/pages/Events/EventSales/InvoiceManageSponsorshipSales";
import CreateEmail from "./components/pages/Emails/CreateEmail/CreateEmail";
import ManageEmails from "./components/pages/Emails/ManageEmails/ManageEmails";
import UpdateEmail from "./components/pages/Emails/UpdateEmail/UpdateEmail";
import CreateInvoice from "./components/pages/Invoicing/CreateInvoice/CreateInvoice";
import ManageInvoices from "./components/pages/Invoicing/ManageInvoices/ManageInvoices";
import UpdateInvoice from "./components/pages/Invoicing/UpdateInvoice/UpdateInvoice";
import Reports from "./components/pages/Reports/Reports";
import Help from "./components/pages/Support/Help";
import StripeConnectCallback from "./components/pages/MyChamber/ChamberSettings/StripeConnectCallback";
import ComingSoon from "./components/pages/ComingSoon/ComingSoon";
import Calculators from "./components/pages/Calculators/Calculators";
import CreateStaffUserPage from "./components/pages/MyChamber/ChamberSettings/usersPermissions/CreateStaffUserPage";
import CmpUserGuide from "./components/pages/UserGuide/CmpUserGuide";
import type { CmpExternalLinkDestination } from "./utils/cmpExternalRouteHref";

const Analytics = React.lazy(
  () => import("./components/pages/MyChamber/Analytics")
);

const analyticsPage = (
  <React.Suspense
    fallback={
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-52 items-center justify-center rounded-md bg-gray-50 text-sm font-semibold text-gray-700"
      >
        Loading analytics…
      </div>
    }
  >
    <Analytics />
  </React.Suspense>
);

// DO NOT use any capital letters in any of the redirect URIs cuz it messes with the exact matches
export enum RoutePaths {
  // common pages
  error500 = "/500",
  login = "/login",
  logout = "/logout",
  newPassword = "/newpassword",
  forgotPassword = "/forgotpassword",
  home = "/",
  secureCheckoutReturn = "/secure-checkout/return",

  // chamber admin pages
  chamberAdmin_myChamber_analytics = "/chamber/analytics",
  chamberAdmin_myChamber_manageChamber = "/chamber/manage",
  chamberAdmin_myChamber_chamberSettings = "/chamber/setup",
  chamberAdmin_myChamber_chamberSettingsStripeConnectCallback = "/chamber/setup/stripe-connect/callback",
  chamberAdmin_myChamber_createStaffUser = "/chamber/setup/users/create",

  // members pages
  chamberAdmin_members_create = "/members/create",
  chamberAdmin_members_manage = "/members/manage",
  chamberAdmin_members_update = "/members/update",
  chamberAdmin_members_updateLegacy = "/members/update/:memberId",

  // prospects pages
  chamberAdmin_prospects_create = "/prospects/create",
  chamberAdmin_prospects_manage = "/prospects/manage",
  chamberAdmin_prospects_update = "/prospects/update/:prospectId",

  // events pages
  chamberAdmin_events_create = "/events/create",
  chamberAdmin_events_manage = "/events/manage",
  chamberAdmin_events_ticketSales = "/events/ticket-sales",
  chamberAdmin_events_ticketSalesCreate = "/events/ticket-sales/create",
  chamberAdmin_events_ticketSalesManage = "/events/ticket-sales/manage",
  chamberAdmin_events_sponsorshipSales = "/events/sponsorship-sales",
  chamberAdmin_events_sponsorshipSalesCreate = "/events/sponsorship-sales/create",
  chamberAdmin_events_sponsorshipSalesManage = "/events/sponsorship-sales/manage",
  chamberAdmin_events_update = "/events/update/:eventId",

  // website pages
  chamberAdmin_website_builder = "/website-builder/access",

  // emails pages
  chamberAdmin_emails_create = "/emails/create",
  chamberAdmin_emails_manage = "/emails/manage",
  chamberAdmin_emails_update = "/emails/update/:emailId",

  // invoicing pages
  chamberAdmin_invoicing_create = "/invoicing/create",
  chamberAdmin_invoicing_createAdvertising = "/invoicing/create/advertising",
  chamberAdmin_invoicing_createCustom = "/invoicing/create/custom",
  chamberAdmin_invoicing_manage = "/invoicing/manage",
  chamberAdmin_invoicing_update = "/invoicing/update/:invoiceId",

  // other pages
  chamberAdmin_reports = "/reports",
  chamberAdmin_surveyEngine = "/survey-engine",
  chamberAdmin_calculators = "/calculators",
  // Design (19) sidebar: the Chamber Perks App™ Dashboard sign-in page.
  chamberAdmin_perks = "https://dashboard.chamberperksapp.com/login",
  chamberAdmin_settings = "/settings",
  chamberAdmin_support = "/support",
  chamberAdmin_guide = "/guide",
}

const LegacyInvoiceCreateRedirect = (): React.ReactElement => {
  const { search } = useLocation();
  const requestedType = new URLSearchParams(search).get("type");

  return (
    <Navigate
      replace
      to={
        requestedType === "custom"
          ? RoutePaths.chamberAdmin_invoicing_createCustom
          : RoutePaths.chamberAdmin_invoicing_createAdvertising
      }
    />
  );
};

const LegacyEventSalesRedirect = ({
  to,
}: {
  to:
    | RoutePaths.chamberAdmin_events_ticketSalesManage
    | RoutePaths.chamberAdmin_events_sponsorshipSalesManage;
}): React.ReactElement => {
  const { search } = useLocation();

  return <Navigate replace to={{ pathname: to, search }} />;
};

// Define which UserTypes we want to include
export type AllowedUserTypes = Extract<
  UserType,
  UserType.ChamberAdmin
  // | UserType.ChamberMemberAdmin
>;

// Helper type to get the correct permission prefix based on UserType
type PermissionPrefix<T extends AllowedUserTypes> =
  T extends UserType.ChamberAdmin
    ? Extract<ChamberMemberProPagePermissionsEnum, `CHAMBER_ADMIN__${string}`>
    : // : T extends UserType.ChamberMemberAdmin
      // ? Extract<
      //     ChamberMemberProPagePermissionsEnum,
      //     `CHAMBER_MEMBER_ADMIN__${string}`
      //   >
      never;

export type NavPlacement = "main" | "footer";

// Update IndividualRoute to be generic
export type IndividualRoute<T extends AllowedUserTypes> = {
  pagePermissionNeeded?: PermissionPrefix<T>;
  anyPagePermissionsNeeded?: PermissionPrefix<T>[];
  requiresChamberPerksDashboardAccess?: boolean;
  /** Hidden unless the chamber bought the Chamber Perks App™ (purchasedItems). */
  requiresChamberPerksAppEntitlement?: boolean;
  requiresWebsiteBuilderEntitlement?: boolean;
  ownerOnly?: boolean;
  // Every staff login may open it (no page permission), e.g. the user guide.
  allStaff?: boolean;
  // The page draws its own full-height layout (no route title header).
  noPageContainer?: boolean;
  path: RoutePaths;
  element: React.ReactNode;
  icon?: string;
  title: string;
  description: string;
  hideInNavBar?: boolean;
  navTitle?: string;
  navDetail?: string;
  navBadge?: string;
  navDividerBefore?: boolean;
  navPlacement?: NavPlacement;
  showInBottomNav?: boolean;
  isExternalLink?: boolean;
  externalLinkDestination?: CmpExternalLinkDestination;
};

// Update IndividualRouteGroup to be generic
export type IndividualRouteGroup<T extends AllowedUserTypes> =
  | {
      nestedRoute: {
        title: string;
        icon: string;
        routes: IndividualRoute<T>[];
      };
      route?: never;
    }
  | {
      route: IndividualRoute<T>;
      nestedRoute?: never;
    };

// Update RouteGroupsType to use the generic versions
type RouteGroupsType = {
  [U in AllowedUserTypes]: IndividualRouteGroup<U>[];
};

const chamberAdminMembersCreatePage = {
  title: "Create Member – Member Information",
  navTitle: "Create Member",
  description:
    "Use this section to create a member manually who has not submitted an online application.",
};

const chamberAdminEventsCreatePage = {
  title: "Create Event",
  description:
    "Use this section to create chamber events, ticket options, registration fields, and sponsorship packages.",
};

const chamberAdminEventsManagePage = {
  title: "Manage Events",
  description:
    "Review event status, revenue, attendees, locations, and open an event to update it.",
};

export const routeGroups: RouteGroupsType = {
  [UserType.ChamberAdmin]: [
    {
      nestedRoute: {
        title: "My Chamber",
        icon: "home",
        routes: [
          {
            anyPagePermissionsNeeded: [
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberGlobalSettings,
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberMembershipSettings,
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberEmailAndCommunicationSettings,
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAffinityProgramSetup,
              ChamberMemberProPagePermissionsEnum.ChamberAdminViewUserActivityLog,
            ],
            path: RoutePaths.chamberAdmin_myChamber_chamberSettings,
            element: <ChamberSettings />,
            title: "Chamber Set Up",
            description:
              "Set your chamber membership model, payment options, approval process, email automation.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
            path: RoutePaths.chamberAdmin_myChamber_analytics,
            element: analyticsPage,
            title: "Analytics Overview",
            description:
              "Review your chamber's membership, events, email, website, and invoicing performance.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberManageChamberProfile,
            path: RoutePaths.chamberAdmin_myChamber_manageChamber,
            element: <ManageChamberProfile />,
            title: "Manage Chamber Profile",
            description: "",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberGlobalSettings,
        path: RoutePaths.chamberAdmin_myChamber_chamberSettingsStripeConnectCallback,
        element: <StripeConnectCallback />,
        title: "Stripe Connect",
        description: "",
        hideInNavBar: true,
      },
    },
    {
      route: {
        ownerOnly: true,
        path: RoutePaths.chamberAdmin_myChamber_createStaffUser,
        element: <CreateStaffUserPage />,
        title: "Create Employee",
        description:
          "Create a chamber staff account and explicitly choose which dashboard areas this person can access.",
        hideInNavBar: true,
      },
    },
    {
      nestedRoute: {
        title: "Prospect Leads",
        icon: "person_add",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminProspectsCreateProspect,
            path: RoutePaths.chamberAdmin_prospects_create,
            element: <CreateProspect />,
            title: "Create Prospect",
            description:
              "Use this section to create a prospect manually who has not submitted an online application.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminProspectsManageProspect,
            path: RoutePaths.chamberAdmin_prospects_manage,
            element: <ManageProspects />,
            title: "Manage Prospects",
            description:
              "Track and manage prospective members. View prospect details, and convert prospects to members.",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminProspectsManageProspect,
        path: RoutePaths.chamberAdmin_prospects_update,
        element: <UpdateProspect />,
        title: "Update Prospect",
        description:
          "Use this section to update a prospect’s information. Click Save button to save all changes. To convert prospect to a member, click Convert to Member button.",
        hideInNavBar: true,
      },
    },
    {
      nestedRoute: {
        title: "Members",
        icon: "person",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMembersCreateMember,
            path: RoutePaths.chamberAdmin_members_create,
            element: <CreateMember />,
            title: chamberAdminMembersCreatePage.title,
            navTitle: chamberAdminMembersCreatePage.navTitle,
            description: chamberAdminMembersCreatePage.description,
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminMembersManageMember,
            path: RoutePaths.chamberAdmin_members_manage,
            element: <ManageMembers />,
            title: "Manage Members",
            description:
              "Use this section to edit and update member information, see renewal dates, membership types and number of employees. Reset member password, suspend and delete member.",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMembersManageMember,
        path: RoutePaths.chamberAdmin_members_update,
        element: <UpdateMember />,
        title: "Update Member",
        description: "",
        hideInNavBar: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminMembersManageMember,
        path: RoutePaths.chamberAdmin_members_updateLegacy,
        element: <UpdateMember />,
        title: "Update Member",
        description: "",
        hideInNavBar: true,
      },
    },
    {
      nestedRoute: {
        title: "Events",
        icon: "calendar",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEventsCreateEvent,
            path: RoutePaths.chamberAdmin_events_create,
            element: <CreateEvent />,
            title: chamberAdminEventsCreatePage.title,
            description: chamberAdminEventsCreatePage.description,
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageEvent,
            path: RoutePaths.chamberAdmin_events_manage,
            element: <ManageEvents />,
            title: chamberAdminEventsManagePage.title,
            description: chamberAdminEventsManagePage.description,
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageTicketSales,
            path: RoutePaths.chamberAdmin_events_ticketSalesCreate,
            element: (
              <InvoiceManageTicketSales
                key="create-ticket-invoice"
                mode="create"
              />
            ),
            title: "Create Ticket Invoice",
            description:
              "Manually create a ticket invoice for an event.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageSponsorshipSales,
            path: RoutePaths.chamberAdmin_events_sponsorshipSalesCreate,
            element: (
              <InvoiceManageSponsorshipSales
                key="create-sponsorship-invoice"
                mode="create"
              />
            ),
            title: "Create Sponsorship Invoice",
            description:
              "Manually create a sponsorship invoice for an event.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageTicketSales,
            path: RoutePaths.chamberAdmin_events_ticketSalesManage,
            element: (
              <InvoiceManageTicketSales
                key="manage-ticket-sales"
                mode="manage"
              />
            ),
            title: "Manage Ticket Sales",
            description: "Manually manage all ticket sales for events.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageSponsorshipSales,
            path: RoutePaths.chamberAdmin_events_sponsorshipSalesManage,
            element: (
              <InvoiceManageSponsorshipSales
                key="manage-sponsorship-sales"
                mode="manage"
              />
            ),
            title: "Manage Sponsorship Sales",
            description:
              "Manually manage all sponsorship sales for events.",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageTicketSales,
        path: RoutePaths.chamberAdmin_events_ticketSales,
        element: (
          <LegacyEventSalesRedirect
            to={RoutePaths.chamberAdmin_events_ticketSalesManage}
          />
        ),
        title: "Manage Ticket Sales",
        description: "",
        hideInNavBar: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageSponsorshipSales,
        path: RoutePaths.chamberAdmin_events_sponsorshipSales,
        element: (
          <LegacyEventSalesRedirect
            to={RoutePaths.chamberAdmin_events_sponsorshipSalesManage}
          />
        ),
        title: "Manage Sponsorship Sales",
        description: "",
        hideInNavBar: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageEvent,
        path: RoutePaths.chamberAdmin_events_update,
        element: <UpdateEvent />,
        title: "Update Event",
        description:
          "Use this section to update event details, tickets, registration fields, sponsorships, and staff tracking notes.",
        hideInNavBar: true,
      },
    },
    {
      nestedRoute: {
        title: "Emails",
        icon: "email",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEmailCreateEmail,
            path: RoutePaths.chamberAdmin_emails_create,
            element: <CreateEmail />,
            title: "Create Emails",
            description:
              "Draft, review, schedule, send, and optionally publish chamber emails to the newsroom.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminEmailManageEmails,
            path: RoutePaths.chamberAdmin_emails_manage,
            element: <ManageEmails />,
            title: "Manage Emails",
            description:
              "Review drafts, scheduled emails, sent campaigns, newsroom status, and delivery metrics.",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminEmailManageEmails,
        path: RoutePaths.chamberAdmin_emails_update,
        element: <UpdateEmail />,
        title: "Update Email",
        description:
          "Update an email draft, review scheduled work, duplicate sent emails, and inspect delivery/newsroom state.",
        hideInNavBar: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminSurveyEngine,
        path: RoutePaths.chamberAdmin_surveyEngine,
        element: <ComingSoon />,
        icon: "assignment_turned_in",
        title: "SurveyEngine™",
        description: "",
        navDetail: "Powered by IDA",
        navBadge: "Coming Soon",
      },
    },
    {
      nestedRoute: {
        title: "Invoices",
        icon: "attach_money",
        routes: [
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminInvoicingCreateInvoice,
            path: RoutePaths.chamberAdmin_invoicing_createAdvertising,
            element: (
              <CreateInvoice
                invoiceType="advertising"
                key="create-advertising-invoice"
              />
            ),
            title: "Create Advertising Invoice",
            description:
              "Create an advertising invoice for a member or customer.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminInvoicingCreateInvoice,
            path: RoutePaths.chamberAdmin_invoicing_createCustom,
            element: (
              <CreateInvoice
                invoiceType="custom"
                key="create-custom-invoice"
              />
            ),
            title: "Create Custom Invoice",
            description: "Create a custom invoice for a member or customer.",
          },
          {
            pagePermissionNeeded:
              ChamberMemberProPagePermissionsEnum.ChamberAdminInvoicingManageInvoices,
            path: RoutePaths.chamberAdmin_invoicing_manage,
            element: <ManageInvoices />,
            title: "Manage Invoices",
            description:
              "View, filter, track, and manage all invoices generated through your chamber.",
          },
        ],
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminInvoicingCreateInvoice,
        path: RoutePaths.chamberAdmin_invoicing_create,
        element: <LegacyInvoiceCreateRedirect />,
        title: "Create Invoice",
        description: "",
        hideInNavBar: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminInvoicingManageInvoices,
        path: RoutePaths.chamberAdmin_invoicing_update,
        element: <UpdateInvoice />,
        title: "Edit Draft Invoice",
        description: "Edit a draft invoice before it is issued.",
        hideInNavBar: true,
      },
    },

    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminReports,
        path: RoutePaths.chamberAdmin_reports,
        element: <Reports />,
        icon: "bar_chart",
        title: "Reports",
        description:
          "View and export your chamber reports to track performance, engagement and revenue.",
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminCalculators,
        path: RoutePaths.chamberAdmin_calculators,
        element: <Calculators />,
        icon: "calculator",
        title: "Calculators",
        description: "",
        navBadge: "Coming Soon",
        navDividerBefore: true,
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminWebsiteManageWebsite,
        requiresWebsiteBuilderEntitlement: true,
        path: RoutePaths.chamberAdmin_website_builder,
        element: null,
        icon: "desktop",
        title: "Website Builder",
        navTitle: "Website Builder Access",
        description: "Open your chamber's Website Builder login page.",
        isExternalLink: true,
        externalLinkDestination: "websiteBuilderAdmin",
      },
    },
    {
      route: {
        requiresChamberPerksDashboardAccess: true,
        requiresChamberPerksAppEntitlement: true,
        path: RoutePaths.chamberAdmin_perks,
        element: <Sample />,
        icon: "card_giftcard",
        title: "Chamber Perks App™",
        description: "",
        showInBottomNav: true,
        isExternalLink: true,
      },
    },
    // {
    //   route: {
    //     pagePermissionNeeded:
    //       ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAnalytics,
    //     path: RoutePaths.chamberAdmin_settings,
    //     element: <Sample />,
    //     icon: "settings",
    //     title: "Settings",
    //     showInBottomNav: true,
    //   },
    // },
    {
      route: {
        // CMP User Guide (Monday 13280799093). The sidebar draws its own
        // guide block; the text comes from the backend.
        allStaff: true,
        noPageContainer: true,
        hideInNavBar: true,
        path: RoutePaths.chamberAdmin_guide,
        element: <CmpUserGuide />,
        icon: "book",
        title: "Chamber Member Pro™ Guide",
        description: "",
      },
    },
    {
      route: {
        pagePermissionNeeded:
          ChamberMemberProPagePermissionsEnum.ChamberAdminSupport,
        path: RoutePaths.chamberAdmin_support,
        element: <Help />,
        icon: "question_circle",
        title: "Help",
        description: "",
        navTitle: "Contact Support",
        // Claude Design (19): the sidebar shows "Need Help?" (Freshdesk) instead;
        // /support stays for old links.
        hideInNavBar: true,
        navPlacement: "footer",
        showInBottomNav: true,
      },
    },
  ],
};

// Derive bottom navigation items from the main routes that have showInBottomNav flag
export const getBottomNavItems = (userType: AllowedUserTypes) => {
  const items: Array<any> = [];

  for (const routeGroup of routeGroups[userType]) {
    if (routeGroup.route && routeGroup.route.showInBottomNav) {
      items.push(routeGroup.route);
    }
  }

  return items;
};

const enumStrings = {
  sessionStorageKey: {
    loggedInWithTemporaryPassword: "loggedInWithTemporaryPassword",
  },
  pages: {
    common: {
      errors: {
        invalidUsernameOrPassword:
          "The email or password is not correct. Check both and try again, or select Forgot your password? to reset your password.",
        networkError: NETWORK_ERROR_TEXT,
        emailDoesNotExist:
          "We could not find an account with this email address. Check the email address and try again. If you still cannot sign in, contact your chamber administrator.",
        tooManyAttempts: "You tried too many times. Wait a few minutes, then try again.",
        invalidVerificationCode:
          "The verification code is not correct. Check the code in the latest email we sent you and try again.",
        userDoesNotHaveAccessToChamberMemberPro: `Your chamber does not have access to Chamber Member Pro. To get access, email ${STAFF_SUPPORT_EMAIL}.`,
      },
      validation: {
        email: "Enter your email address.",
        password: "Enter your password.",
        code: "Enter the verification code from your email.",
        newPassword: "Enter a new password.",
        confirmPassword: "Enter your new password again.",
        passwordsMustMatch: "The passwords do not match. Enter the same password in both fields.",
        // Matches commonCognitoPasswordRegex: + = ' " and \ do not count as
        // symbols there, and spaces are not allowed.
        mustContain8Characters:
          "Use at least 8 characters with an uppercase letter, a lowercase letter, a number, and a symbol such as ! @ # $ or %. The symbols + = ' \" and \\ do not count. Do not use spaces.",
      },
      buttons: {
        goBackToLoginText: "Back to sign in",
        homeButtonText: "Go Home",
        contactSupportButtonText: "Contact Support",
      },
    },
    login: {
      forgotPasswordText: "Forgot your password?",
      logoSubtitle: "Sign in to your account",
      buttonText: "Sign In",
    },
    newPassword: {
      logoSubtitle: "Set your password",
      description:
        "You have entered a temporary single-use password. Please set a new permanent password for your account.",
      buttonText: "Set Password",
    },
    forgotPassword: {
      logoSubtitle: "Reset your password",
      description:
        "Enter your account email and we will send you a code to reset your password.",
      buttonText: "Continue",
    },
    resetPassword: {
      pleaseVerifyEmailText: (email: string) =>
        `Please enter the verification code sent to ${email} and your new password.`,
      resetPasswordSuccessText: "Your password has been successfully reset.",
      buttonText: "Reset Password",
      signInButton: "Sign In",
    },
    error500: {
      title: "500",
      subtitle: "An Error Has Occurred",
      description: `Chamber Member Pro could not finish your last action, so your last change may not be saved. Go back, check your change, and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
      emailSubject: "Chamber Member Pro 500 Error",
    },
    error404: {
      title: "404",
      subtitle: "Page Not Found",
      description:
        "Sorry, the page you are looking for doesn't exist or has been moved.",
      emailSubject: "Chamber Member Pro 404 Error",
    },
    // A real dashboard page that this user's permissions do not include.
    noPageAccess: {
      eyebrow: "Permissions",
      subtitle: "No Access",
      description:
        "You do not have access to this page. Ask your chamber administrator for access.",
      emailSubject: "Chamber Member Pro Page Access",
    },
  },
};

export default enumStrings;
